/**
 * Whether a Draft submit may skip the deliverables dialog.
 *
 * Extracted from `RequestDetail.handleStageChangeClick` rather than left inline, because the
 * rule is the whole risk of the feature and the screen it lives on cannot test it: the
 * `request-detail-dirty-fields` suite deliberately does not flush the deliverables load, so
 * a request with saved deliverables and no open edit session is not reachable there.
 *
 * The dialog does three jobs. Only the first is redundant when the user is already looking
 * at the Deliverables step:
 *
 *  1. Showing the deliverables. Redundant on that step, which is the point of the feature.
 *  2. **Being the submit confirmation** (FR11). Never skipped — the caller falls through to
 *     the ordinary stage-change confirmation, so submitting is still a deliberate act.
 *  3. **Enforcing at least one deliverable** (FR21a). Never skipped: a non-empty list is a
 *     condition on skipping, not a consequence of it.
 *
 * An earlier draft also refused to skip while types were ticked but not committed by a
 * second "Add all checked" press. That was removed on 2026-08-12 after review showed the
 * premise was wrong — `RequestDeliverablesDialog.handleConfirm` gates ONLY on an empty list,
 * so there was no second protection to preserve. The state itself is now gone as well: the
 * editor commits a tick straight to the caller's pending list, so a ticked type is a
 * deliverable and `deliverableCount` already sees it.
 */
export interface DeliverablesSubmitDialogContext {
  /** Normalized current step id, e.g. from `normalizeRequestFormStepId`. */
  stepId: string;
  /** How many deliverables the request holds, counting anything pending in this session. */
  deliverableCount: number;
}

export const DELIVERABLES_STEP_ID = 'deliverables';

export const shouldSkipDeliverablesDialog = ({
  stepId,
  deliverableCount,
}: DeliverablesSubmitDialogContext): boolean =>
  stepId === DELIVERABLES_STEP_ID && deliverableCount > 0;

/**
 * Title and confirm label for the stage-change confirmation.
 *
 * A Draft reaches this dialog only through the skip above, where it is acting as the SUBMIT
 * confirmation. It therefore has to speak the language of the button that opened it:
 * `draftSubmitLabel` is already "Submit" or "Send to Vertiv" depending on the requestor,
 * and exists precisely so the two cannot drift (FR11). Asking someone who pressed "Send to
 * Vertiv" to "Change Stage" hands an external requestor the internal stage vocabulary this
 * batch exists to keep out of their view.
 *
 * Every other stage keeps the title it has always had; the confirm action is labeled
 * "Continue" so approvers and internal users are not asked to "Change Stage".
 */
export const getStageConfirmationCopy = (
  isDraftSubmit: boolean,
  draftSubmitLabel: string,
  isApproveSubmitToVertiv = false,
): { title: string; confirmText: string; message?: string } => {
  if (isApproveSubmitToVertiv) {
    return {
      title: 'Confirm Submit To Vertiv',
      confirmText: 'Continue',
      message:
        'Continuing will submit this request to Vertiv and mark it as In Progress.',
    };
  }
  return isDraftSubmit
    ? { title: 'Confirm Submit', confirmText: draftSubmitLabel }
    : { title: 'Confirm Stage Change', confirmText: 'Continue' };
};
